import { ArrowRight, Check, Loader2 } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";
import {
	type BillingPeriod,
	formatAccountLimit,
	formatLimit,
	getMonthlyPrice,
	PLAN_CAPABILITIES,
	SUBSCRIPTION_DEFINITIONS,
} from "@/lib/plans";
import { startSubscriptionCheckout } from "@/lib/subscription-checkout";
import { cn } from "@/lib/utils";

type SelectablePlan = "pro" | "max";

const PLAN_TAGLINES: Record<SelectablePlan, string> = {
	pro: "Best for solo, steady publishing.",
	max: "For agencies, teams, and multi-brand calendars.",
};

const TRUST_PILLS = [
	"30 day money-back guarantee",
	"Secure checkout through Stripe",
	"Cancel anytime, from settings",
];

const PLAN_FAQS = [
	{
		question: "What if it is not for me?",
		answer:
			"Email us within 30 days of your first payment and we refund it in full.",
	},
	{
		question: "Do I need an account before checkout?",
		answer: "No. Choose a plan first, then create your account after checkout.",
	},
	{
		question: "Can I switch plans later?",
		answer: "Yes. You can move between Pro and Max from billing settings.",
	},
];

const CHECKOUT_CLIENT_ID_STORAGE_KEY = "agentreacher:checkout-client-id";
const CHECKOUT_URL_STORAGE_KEY = "agentreacher:checkout-url";
const CHECKOUT_URL_MAX_AGE_MS = 23 * 60 * 60 * 1000;

function buildPlanFeatures(plan: SelectablePlan): string[] {
	const cap = PLAN_CAPABILITIES[plan];
	if (plan === "pro") {
		return [
			`${formatAccountLimit(cap.maxConnectedSocialAccounts)} connected accounts`,
			`${formatLimit(cap.maxScheduledPosts)} scheduled posts / mo`,
			`${cap.maxWorkspaces} workspaces`,
			`${cap.maxTeamMembers} team members`,
			"Email support",
		];
	}

	return [
		`${formatAccountLimit(cap.maxConnectedSocialAccounts)} connected accounts`,
		`${formatLimit(cap.maxScheduledPosts)} scheduled posts / mo`,
		`${cap.maxWorkspaces} workspace${cap.maxWorkspaces > 1 ? "s" : ""}`,
		`${cap.maxTeamMembers} team member${cap.maxTeamMembers > 1 ? "s" : ""}`,
		"Team approvals + priority support",
	];
}

function appendRedirect(url: string, redirect: string | null | undefined) {
	if (!redirect) return url;
	const nextUrl = new URL(url);
	nextUrl.searchParams.set("redirect", redirect);
	return nextUrl.toString();
}

function getStoredCheckoutUrl(): string | null {
	try {
		const raw = window.localStorage.getItem(CHECKOUT_URL_STORAGE_KEY);
		if (!raw) return null;

		const parsed = JSON.parse(raw) as { url?: unknown; startedAt?: unknown };
		if (
			typeof parsed.url !== "string" ||
			typeof parsed.startedAt !== "number" ||
			Date.now() - parsed.startedAt > CHECKOUT_URL_MAX_AGE_MS
		) {
			window.localStorage.removeItem(CHECKOUT_URL_STORAGE_KEY);
			return null;
		}

		return parsed.url;
	} catch {
		window.localStorage.removeItem(CHECKOUT_URL_STORAGE_KEY);
		return null;
	}
}

function storeCheckoutUrl(url: string) {
	window.localStorage.setItem(
		CHECKOUT_URL_STORAGE_KEY,
		JSON.stringify({ url, startedAt: Date.now() }),
	);
}

function getCheckoutClientId() {
	const existing = window.localStorage.getItem(CHECKOUT_CLIENT_ID_STORAGE_KEY);
	if (existing) return existing;

	const checkoutClientId = crypto.randomUUID();
	window.localStorage.setItem(CHECKOUT_CLIENT_ID_STORAGE_KEY, checkoutClientId);
	return checkoutClientId;
}

export function PlanChooser({ redirect }: { redirect?: string | null }) {
	const [isPending, setIsPending] = React.useState(false);
	const [loadingPlan, setLoadingPlan] = React.useState<SelectablePlan | null>(
		null,
	);
	const [billingPeriod, setBillingPeriod] =
		React.useState<BillingPeriod>("monthly");
	const [error, setError] = React.useState<string | null>(null);
	const checkoutInFlightRef = React.useRef(false);

	const resetCheckoutState = React.useCallback(() => {
		checkoutInFlightRef.current = false;
		setIsPending(false);
		setLoadingPlan(null);
	}, []);

	React.useEffect(() => {
		const resetIfPageIsActive = () => {
			if (document.visibilityState === "visible") resetCheckoutState();
		};

		window.addEventListener("pageshow", resetCheckoutState);
		window.addEventListener("focus", resetCheckoutState);
		document.addEventListener("visibilitychange", resetIfPageIsActive);

		return () => {
			window.removeEventListener("pageshow", resetCheckoutState);
			window.removeEventListener("focus", resetCheckoutState);
			document.removeEventListener("visibilitychange", resetIfPageIsActive);
		};
	}, [resetCheckoutState]);

	const redirectToCheckout = React.useCallback(
		(checkoutUrl: string) => {
			window.location.assign(checkoutUrl);
			window.setTimeout(resetCheckoutState, 1500);
		},
		[resetCheckoutState],
	);

	const handlePlanSelect = async (plan: SelectablePlan) => {
		if (checkoutInFlightRef.current) return;
		checkoutInFlightRef.current = true;
		setIsPending(true);
		setLoadingPlan(plan);
		setError(null);
		try {
			const storedCheckoutUrl = getStoredCheckoutUrl();
			if (storedCheckoutUrl) {
				redirectToCheckout(storedCheckoutUrl);
				return;
			}

			const checkoutUrl = await startSubscriptionCheckout({
				plan,
				billingPeriod,
				checkoutClientId: getCheckoutClientId(),
				successUrl: appendRedirect(
					`${window.location.origin}/upgrade-success?plan=${plan}`,
					redirect,
				),
				cancelUrl: appendRedirect(
					`${window.location.origin}/upgrade-error?reason=cancelled&plan=${plan}&billingPeriod=${billingPeriod}`,
					redirect,
				),
			});
			storeCheckoutUrl(checkoutUrl);
			redirectToCheckout(checkoutUrl);
		} catch (err) {
			const message =
				err instanceof Error ? err.message : "Failed to start checkout";
			console.error("[PlanChooser] checkout failed", err);
			setError(message);
			toast.error(message);
			resetCheckoutState();
		}
	};

	return (
		<div className="space-y-5">
			<div className="flex justify-center">
				<div className="inline-flex rounded-full border border-border bg-muted/40 p-1 text-sm">
					<button
						type="button"
						onClick={() => setBillingPeriod("monthly")}
						className={cn(
							"rounded-full px-4 py-1.5 font-medium transition-colors",
							billingPeriod === "monthly"
								? "bg-background text-foreground shadow-sm"
								: "text-muted-foreground hover:text-foreground",
						)}
					>
						Monthly
					</button>
					<button
						type="button"
						onClick={() => setBillingPeriod("yearly")}
						className={cn(
							"flex items-center gap-1.5 rounded-full px-4 py-1.5 font-medium transition-colors",
							billingPeriod === "yearly"
								? "bg-background text-foreground shadow-sm"
								: "text-muted-foreground hover:text-foreground",
						)}
					>
						Annually
						<span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400">
							2 months free
						</span>
					</button>
				</div>
			</div>

			{error && <p className="text-center text-sm text-red-500">{error}</p>}

			<div className="grid gap-4 md:grid-cols-2">
				<SelectablePlanButton
					plan="pro"
					ctaLabel="Subscribe to Pro"
					billingPeriod={billingPeriod}
					features={buildPlanFeatures("pro")}
					loading={loadingPlan === "pro"}
					disabled={isPending}
					onClick={() => void handlePlanSelect("pro")}
				/>
				<SelectablePlanButton
					plan="max"
					ctaLabel="Subscribe to Max"
					billingPeriod={billingPeriod}
					features={buildPlanFeatures("max")}
					loading={loadingPlan === "max"}
					disabled={isPending}
					onClick={() => void handlePlanSelect("max")}
				/>
			</div>

			<div className="mx-auto grid w-full max-w-3xl gap-2 border-t border-border pt-4 sm:grid-cols-3">
				{TRUST_PILLS.map((pill) => (
					<span
						key={pill}
						className="inline-flex min-h-9 items-center justify-center gap-2 rounded-md bg-muted/35 px-3 text-center text-xs font-medium text-muted-foreground"
					>
						<Check className="size-3.5 shrink-0 text-primary" />
						{pill}
					</span>
				))}
			</div>

			<div className="mx-auto w-full max-w-3xl overflow-hidden rounded-lg border border-border bg-background text-left">
				{PLAN_FAQS.map((item, index) => (
					<div
						key={item.question}
						className={cn(
							"flex flex-col gap-1 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6",
							index > 0 && "border-t border-border",
						)}
					>
						<div className="text-sm font-semibold text-foreground">
							{item.question}
						</div>
						<p className="text-xs leading-5 text-muted-foreground sm:text-right">
							{item.answer}
						</p>
					</div>
				))}
			</div>
		</div>
	);
}

function SelectablePlanButton({
	plan,
	ctaLabel,
	billingPeriod,
	features,
	loading,
	disabled,
	onClick,
}: {
	plan: SelectablePlan;
	ctaLabel: string;
	billingPeriod: BillingPeriod;
	features: string[];
	loading: boolean;
	disabled: boolean;
	onClick: () => void;
}) {
	const sub = SUBSCRIPTION_DEFINITIONS[plan];
	const monthlyPrice = getMonthlyPrice(plan, billingPeriod);
	const displayPrice =
		billingPeriod === "yearly" ? sub.yearlyPriceEUR : monthlyPrice;
	const isMax = plan === "max";

	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			className={cn(
				"flex h-full min-h-[24rem] flex-col rounded-lg border p-4 text-left transition-colors disabled:pointer-events-none disabled:opacity-60",
				isMax
					? "border-2 border-primary bg-primary/[0.04] shadow-sm hover:bg-primary/[0.07]"
					: "border-border bg-muted/20 hover:border-primary/40 hover:bg-muted/40",
			)}
		>
			<div className="grid min-h-[6.5rem] grid-cols-[1fr_auto] items-start gap-3">
				<div className="min-w-0">
					<div className="text-lg font-semibold text-foreground">
						{sub.name}
					</div>
					<p className="mt-1 max-w-[16rem] text-sm leading-6 text-muted-foreground">
						{PLAN_TAGLINES[plan]}
					</p>
				</div>
				<div className="whitespace-nowrap text-right text-base font-semibold text-foreground">
					€{displayPrice}
					<span className="block text-xs font-normal text-muted-foreground">
						{billingPeriod === "yearly" ? "per year" : "per month"}
					</span>
				</div>
			</div>
			<ul className="mt-4 space-y-2 text-sm leading-6 text-foreground">
				{features.map((feature) => (
					<li key={feature} className="flex items-start gap-2">
						<Check className="mt-1 size-4 shrink-0 text-primary" />
						<span>{feature}</span>
					</li>
				))}
			</ul>
			<div className="mt-auto space-y-4 pt-5">
				<p className="text-sm text-muted-foreground">
					Billed {billingPeriod === "yearly" ? "yearly" : "monthly"}. Cancel
					anytime.
				</p>
				<span
					className={cn(
						"inline-flex h-10 w-full items-center justify-center gap-2 rounded-md px-3 text-sm font-medium",
						isMax
							? "bg-primary text-primary-foreground"
							: "border border-border bg-background text-foreground",
					)}
				>
					{loading ? (
						<>
							<Loader2 className="size-4 animate-spin" />
							Opening Stripe...
						</>
					) : (
						<>
							{ctaLabel}
							<ArrowRight className="size-4" />
						</>
					)}
				</span>
			</div>
		</button>
	);
}
